import type { FilterGroup } from '../../core/types/workspaceTypes';
import type { EnumLookupTarget } from '@/types/electron';

/**
 * The outcome of parsing typed cell / filter text (S4 review F, F2): a value, or why the text is not one. A codec
 * never throws — an editor keeps an invalid draft open and shows `error` instead of committing anything.
 */
export type TableExplorerCellParseResult = { ok: true; value: unknown } | { ok: false; error: string };

export type TableExplorerCellValueCodec = {
  tagForCell?: (row: Record<string, unknown>, columnName: string) => string | undefined;
  format: (value: unknown, tag?: string) => string;
  parse: (text: string, tag?: string, context?: { empty: boolean }) => TableExplorerCellParseResult;
  /**
   * Editor control for ONE cell, from its own tag. Omitted = the column
   * dataType picks the control (SQL default). A host whose value types vary
   * per row (Azure Table properties) must supply it — S4 review B, R4.
   */
  inputKind?: (tag?: string) => 'text' | 'number' | 'boolean' | 'datetime';
};

export type TableExplorerMetadataTable = {
  schema: string;
  name: string;
  columns: Array<{ name: string; dataType: string }>;
};

export type TableExplorerMetadataEditorProviders = {
  /**
   * Host cache identity (S4 review C, M5; review D, D10): every host-resolved label, row, point read AND metadata
   * listing is cached under it, and the host changes it whenever what it resolves may have changed — a reconnect
   * (new connection generation) or a mutation on the connection. SQL hosts never supply one, so their keys are
   * untouched. Azure Table: `['azure-storage', connectionId, generation, revision]`, i.e. under the storage
   * invalidation prefix.
   */
  cacheScope?: readonly (string | number)[];
  listTables: (input: { connectionId: string; database: string }) => Promise<TableExplorerMetadataTable[]>;
  listColumns: (input: {
    connectionId: string;
    database: string;
    schema: string;
    table: string;
  }) => Promise<Array<{ name: string; dataType: string; isPrimaryKey?: boolean }>>;
};

/** Query key of a host metadata listing, under the host's cache scope (review D, D10). */
export const hostMetadataQueryKey = (
  providers: TableExplorerMetadataEditorProviders,
  connectionId: string | null | undefined,
  kind: 'tables' | 'lookup-target-columns' | 'candidate-columns' | 'source-columns',
  ...parts: Array<string | null | undefined>
): readonly unknown[] => [...(providers.cacheScope ?? ['host-metadata', connectionId]), kind, ...parts];

export type TableExplorerHostLookupDeclaration = {
  lookup: EnumLookupTarget;
  lookupMode?: 'cached' | 'on-demand';
  targetFilter?: FilterGroup[];
  declarationId: string;
  /**
   * The constraint cached lookup pairs must have been extracted within (S4 review D, D4) — compared with
   * `EnumValueCache.lookup.scope`. From the port's `lookupPairsScope`; absent = no constraint to honour.
   */
  pairsScope?: string;
};

/**
 * Lossless, JSON-safe text identity for a host cache key (S4 review C, M2).
 * TanStack hashes query keys with `JSON.stringify`, which throws on `bigint`
 * (Edm.Int64 values); a registry key must also tell `1n` from `'1'`. Tagged
 * shapes keep every scalar distinct: bigint → `{ "$int64": "<decimal>" }`,
 * Date → `{ "$date": "<iso>" }`, `undefined` → `{ "$undefined": true }`.
 */
export function hostCacheIdentity(value: unknown): string {
  return JSON.stringify(value === undefined ? { $undefined: true } : value, function replace(this: unknown, key: string, item: unknown) {
    const raw = key === '' ? item : (this as Record<string, unknown>)[key];
    if (typeof raw === 'bigint') return { $int64: raw.toString() };
    if (raw instanceof Date) return { $date: raw.toISOString() };
    if (raw === undefined) return { $undefined: true };
    return item;
  });
}

export type TableExplorerRelationshipPort = TableExplorerMetadataEditorProviders & {
  resolveRow: (input: {
    connectionId: string;
    database: string;
    referencedSchema: string;
    referencedTable: string;
    referencedColumn: string;
    value: unknown;
    targetFilter?: FilterGroup[];
    declarationId?: string;
  }) => Promise<{
    row: Record<string, unknown> | null;
    columns: Array<{ name: string; dataType: string }>;
    /** Presentation only (last UI/UX pass, F14): the host grid's display seam for the popover's values. */
    displayText?: (value: unknown, columnName: string) => string | undefined;
  }>;
  resolveLabels: (input: {
    connectionId: string;
    database: string;
    table: string;
    keyColumn: string;
    labelColumn: string;
    keys: readonly unknown[];
    targetFilter?: FilterGroup[];
    declarationId?: string;
  }) => Promise<Map<string, string | null>>;
  openReferencedRow: (input: {
    connectionId: string;
    database: string;
    referencedSchema: string;
    table: string;
    referencedColumn: string;
    value: unknown;
    targetFilter?: FilterGroup[];
    declarationId?: string;
    row?: Record<string, unknown>;
  }) => void | Promise<void>;
  /**
   * Why ONE relationship value cannot be navigated / point-read (S4 review F, F11), or `undefined` when it can.
   * The FK popover shows it next to an unavailable "Open" instead of silently doing nothing.
   */
  unresolvableReason: (input: {
    referencedColumn: string;
    value: unknown;
    targetFilter?: FilterGroup[];
    declarationId?: string;
  }) => string | undefined;
  /** The identity of the constraint a relationship's lookup pairs belong to (S4 review D, D4; Azure Table). */
  lookupPairsScope?: (declaration: { declarationId: string; lookup: EnumLookupTarget; targetFilter?: FilterGroup[] }) => string | undefined;
  /**
   * Candidate rows of a relationship target for the FK row picker (S4 review
   * C, M7), so a host picker never reaches `fetchTableExplorerRows`. Azure
   * Table: one guarded page of the target table scoped by the declaration's
   * target filter; the picker searches and sorts that page locally.
   */
  searchRows?: (input: {
    connectionId: string;
    database: string;
    schema: string;
    table: string;
    targetFilter?: FilterGroup[];
    limit: number;
  }) => Promise<{ rows: Record<string, unknown>[]; complete: boolean }>;
};
